import { css } from 'aphrodite';
import * as React from 'react';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { formatCount, formatDayLabel, type VolumeTrendPoint } from './reportsPresentation';
import { reportsStyles as rs } from './styles/reportsStyles';

const CHART_AXIS_TICK = { fontSize: THEME.typography.fontSizes.xxs, fill: THEME.colors.text.light } as const;

function VolumeTrendTooltip({
	active,
	payload,
}: {
	active?: boolean;
	payload?: ReadonlyArray<{ payload: VolumeTrendPoint }>;
}) {
	if (!active || !payload?.length) return null;
	const point = payload[0]?.payload;
	if (!point) return null;
	return (
		<div className={css(rs.chartTooltip)}>
			<div className={css(rs.chartTooltipTitle)}>{formatDayLabel(point.date)}</div>
			<div className={css(rs.chartTooltipRow)}>
				<span className={css(rs.legendSwatch, rs.legendSwatchCurrent)} />
				Current · {formatCount(point.current)}
			</div>
			{point.previous != null && (
				<div className={css(rs.chartTooltipRow)}>
					<span className={css(rs.legendSwatch, rs.legendSwatchPrevious)} />
					Previous ({formatDayLabel(point.previousDate)}) · {formatCount(point.previous)}
				</div>
			)}
		</div>
	);
}

interface VolumeTrendCardProps {
	data: VolumeTrendPoint[];
	isLoading?: boolean;
}

// Past this many points, per-point dots overlap into a solid band; show the bare line instead.
const DOTTED_SERIES_POINTS_MAX = 14;

export const VolumeTrendCard: React.FC<VolumeTrendCardProps> = ({ data, isLoading }) => {
	const gradientId = `supportVolumeGrad-${React.useId().replaceAll(':', '')}`;
	const isEmpty = data.every(point => point.current === 0 && (point.previous ?? 0) === 0);
	const showDots = data.length <= DOTTED_SERIES_POINTS_MAX;

	return (
		<section className={css(rs.card)}>
			<div className={css(rs.cardHeader)}>
				<div>
					<h2 className={css(rs.cardTitle)}>Conversations created</h2>
					<div className={css(rs.cardSubtitle)}>Daily volume vs. previous period</div>
				</div>
				<div className={css(rs.legendRow)}>
					<span className={css(rs.legendItem)}>
						<span className={css(rs.legendSwatch, rs.legendSwatchCurrent)} />
						Current
					</span>
					<span className={css(rs.legendItem)}>
						<span className={css(rs.legendSwatch, rs.legendSwatchPrevious)} />
						Previous
					</span>
				</div>
			</div>
			{isLoading ? (
				<div className={css(rs.chartLoading)}>Loading…</div>
			) : isEmpty ? (
				<div className={css(rs.chartEmpty)}>No conversations in this period.</div>
			) : (
				<ResponsiveContainer width='100%' height={260}>
					<ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
						<defs>
							<linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
								<stop offset='0%' stopColor={THEME.colors.chart.levBlue} stopOpacity={0.1} />
								<stop offset='100%' stopColor={THEME.colors.chart.levBlue} stopOpacity={0} />
							</linearGradient>
						</defs>
						<CartesianGrid stroke={THEME.colors.border.light} vertical={false} />
						<XAxis dataKey='label' tick={CHART_AXIS_TICK} tickLine={false} axisLine={false} minTickGap={24} />
						<YAxis allowDecimals={false} tick={CHART_AXIS_TICK} tickLine={false} axisLine={false} width={36} />
						<Tooltip content={<VolumeTrendTooltip />} cursor={{ stroke: THEME.colors.border.light }} />
						<Area
							type='monotone'
							dataKey='current'
							stroke='none'
							fill={`url(#${gradientId})`}
							isAnimationActive={false}
						/>
						<Line
							type='monotone'
							dataKey='previous'
							stroke={THEME.colors.chart.aceGray}
							strokeWidth={2}
							strokeLinecap='round'
							dot={false}
							isAnimationActive={false}
						/>
						<Line
							type='monotone'
							dataKey='current'
							stroke={THEME.colors.chart.levBlue}
							strokeWidth={2}
							strokeLinecap='round'
							dot={showDots ? { r: 3.5, fill: THEME.colors.chart.levBlue, strokeWidth: 0 } : false}
							activeDot={{ r: 4.5 }}
							isAnimationActive={false}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			)}
		</section>
	);
};
